import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2, Search, LayoutGrid, List, Star, IndianRupee, CalendarCheck, Pencil, Camera, Save, X, Clock, User, RotateCcw, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";
import AttendanceRangeView from "@/pages/star/AttendanceRangeView";
import { RecordHealth } from "@/pages/admin/RecordHealth";
import { LeftControl } from "@/pages/admin/LeftControl";
import { DiaryHistory } from "@/pages/star/diary/DiaryHistory";
import { IncompleteProfiles } from "@/components/IncompleteProfiles";

const AdminDiaryHistory = ({ sid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen((v) => !v)} data-testid="detail-diary-history">{open ? "Hide diary history" : "Diary history"}</Button>
      {open && <div className="mt-2 rounded-lg bg-slate-50 p-2"><DiaryHistory url={`/admin/diary/history/${sid}`} pdfUrl={`/admin/diary/history/${sid}/pdf`} photoBase="/admin/diary/photo" /></div>}
    </div>
  );
};

const LeftBadge = ({ s }) => s.left ? <Badge variant="outline" className="text-[10px] bg-slate-200 text-slate-700 border-slate-400" data-testid={`left-badge-${s.id}`}>Left</Badge> : null;

const fmtIST = (iso) => iso ? formatDateTime(iso) : "—";
const ACCT = {
  activated: { label: "Activated", cls: "bg-green-100 text-green-800 border-green-300" },
  temp_active: { label: "Temp password", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  needs_password: { label: "Needs password", cls: "bg-amber-100 text-amber-800 border-amber-300" },
};
const compColor = (p) => (p >= 80 ? "bg-green-500" : p >= 50 ? "bg-amber-500" : "bg-red-500");

function AuthImg({ sid, hasPhoto, name, className, v = 0 }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!hasPhoto) return;
    let active = true; let obj = "";
    api.get(`/student-photo/${sid}`, { responseType: "blob", params: v ? { v } : undefined })
      .then((r) => { if (!active) return; obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [sid, hasPhoto, v]);
  if (url) return <img src={url} alt={name} className={className + " object-cover"} />;
  return <div className={className + " bg-slate-200 flex items-center justify-center text-slate-500 font-bold text-lg"}>{(name || "?").charAt(0).toUpperCase()}</div>;
}

function CompletionBar({ pct, testid }) {
  return (
    <div className="w-full" data-testid={testid}>
      <div className="flex justify-between text-[11px] text-slate-500 mb-0.5"><span>Profile</span><span className="font-semibold">{pct}%</span></div>
      <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden"><div className={`h-full ${compColor(pct)}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function StudentDetail({ s, onClose, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [logins, setLogins] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [photoBust, setPhotoBust] = useState(0);

  useEffect(() => { setForm({ name: s.name || "", date_of_birth: s.date_of_birth || "", parent_name: s.parent_name || "", parent_contact: s.parent_contact || "", parent_contact_alt: s.parent_contact_alt || "", guardian_email: s.guardian_email || "" }); setEdit(false); }, [s]);
  const loadLogins = useCallback(async () => {
    try { const { data } = await api.get(`/admin/students/${s.id}/logins`, { params: { frm: from, to } }); setLogins(data.events || []); } catch { /* ignore */ }
  }, [s.id, from, to]);
  useEffect(() => { loadLogins(); }, [loadLogins]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if ((form.parent_contact || "") !== (s.parent_contact || "")) { payload.confirmation_method = "in_person"; payload.confirmation_note = "Updated by admin from Students directory"; }
      await api.post(`/admin/students/${s.id}/profile`, payload);
      toast.success("Profile updated"); setEdit(false); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/admin/students/${s.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo updated"); setPhotoBust((x) => x + 1); onSaved && onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not upload photo"); }
  };
  const [rotating, setRotating] = useState(false);
  const rotatePhoto = async (direction) => {
    setRotating(true);
    try { await api.post(`/admin/students/${s.id}/photo/rotate`, { direction }); setPhotoBust((x) => x + 1); toast.success("Photo rotated"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not rotate photo"); } finally { setRotating(false); }
  };
  const acct = ACCT[s.account_status] || ACCT.needs_password;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="student-detail">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><User className="h-5 w-5" /> {s.name || "Student"}</DialogTitle><DialogDescription>Roll {s.roll_number} · {s.student_class}</DialogDescription></DialogHeader>
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <div className="relative">
            <AuthImg key={photoBust} v={photoBust} sid={s.id} hasPhoto={s.has_photo || photoBust > 0} name={s.name} className="h-24 w-24 rounded-2xl" />
            <label className="absolute -bottom-2 -right-2 bg-blue-900 text-white rounded-full p-1.5 cursor-pointer hover:bg-blue-950" title="Upload photo" data-testid="detail-photo-upload">
              <Camera className="h-4 w-4" /><input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadPhoto} />
            </label>
            </div>
            {(s.has_photo || photoBust > 0) && (
              <div className="mt-3 flex gap-1" data-testid="detail-photo-rotate">
                <Button type="button" size="sm" variant="outline" className="h-8 px-2" disabled={rotating} onClick={() => rotatePhoto("left")} title="Rotate left" data-testid="detail-photo-rotate-left"><RotateCcw className="h-4 w-4" /></Button>
                <Button type="button" size="sm" variant="outline" className="h-8 px-2" disabled={rotating} onClick={() => rotatePhoto("right")} title="Rotate right" data-testid="detail-photo-rotate-right">{rotating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}</Button>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className={acct.cls} data-testid="detail-account-status">{acct.label}</Badge>
              <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-300"><Star className="h-3 w-3 mr-1" /> {s.star_balance} stars</Badge>
              <Badge variant="outline" className={s.fees_outstanding > 0 ? "bg-red-50 text-red-700 border-red-300" : "bg-green-50 text-green-700 border-green-300"}><IndianRupee className="h-3 w-3 mr-1" /> {s.fees_outstanding > 0 ? `₹${s.fees_outstanding} due` : "No dues"}</Badge>
              <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300"><CalendarCheck className="h-3 w-3 mr-1" /> {s.attendance?.pct != null ? `${s.attendance.pct}% (${s.attendance.present}/${s.attendance.total})` : "No attendance"}</Badge>
            </div>
            <CompletionBar pct={s.completion} testid="detail-completion" />
            <div className="text-xs text-slate-500">Teacher(s): {s.teachers?.length ? s.teachers.join(", ") : "Unassigned"}</div>
          </div>
        </div>
        <LeftControl s={s} onChanged={() => { onSaved && onSaved(); onClose(); }} />
        <AdminDiaryHistory sid={s.id} />

        <div className="flex items-center justify-between mt-4 mb-1">
          <h3 className="font-semibold text-slate-800">Profile details</h3>
          {edit ? (
            <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => setEdit(false)}>Cancel</Button><Button size="sm" className="bg-blue-900 hover:bg-blue-950" onClick={save} disabled={busy} data-testid="detail-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}</Button></div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="detail-edit"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
          )}
        </div>
        {edit ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {[["name", "Name"], ["date_of_birth", "Date of birth (YYYY-MM-DD)"], ["parent_name", "Parent name"], ["parent_contact", "Parent contact"], ["parent_contact_alt", "Alternate contact"], ["guardian_email", "Parent/guardian email"]].map(([k, lbl]) => (
              <div key={k}><Label className="text-xs">{lbl}</Label><Input value={form[k] || ""} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} data-testid={`detail-field-${k}`} /></div>
            ))}
            <p className="text-[11px] text-slate-400 sm:col-span-2">Changing the parent contact is logged with an admin confirmation note.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <Field label="Roll" value={s.roll_number} /><Field label="Class" value={s.student_class} />
            <Field label="Date of birth" value={s.date_of_birth} /><Field label="Parent name" value={s.parent_name} />
            <Field label="Parent contact" value={s.parent_contact} /><Field label="Alternate contact" value={s.parent_contact_alt} />
            <Field label="Parent email" value={s.guardian_email} /><Field label="Last login" value={fmtIST(s.last_login)} />
          </div>
        )}

        <div className="mt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h3 className="font-semibold text-slate-800 flex items-center gap-1.5"><Clock className="h-4 w-4" /> Login activity</h3>
            <div className="flex items-center gap-1.5 text-xs">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-[140px]" data-testid="logins-from" />
              <span className="text-slate-400">to</span>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-[140px]" data-testid="logins-to" />
              {(from || to) && <Button size="sm" variant="ghost" className="h-8" onClick={() => { setFrom(""); setTo(""); }}>Clear</Button>}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 max-h-48 overflow-y-auto" data-testid="logins-list">
            {logins.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No logins recorded in this range.</p>
              : logins.map((l, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 text-sm last:border-0">
                  <span>{fmtIST(l.at)}</span><span className="text-xs text-slate-400">{l.ip || ""}</span>
                </div>
              ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">We record each successful sign-in. Sign-out time isn't tracked (sessions are stateless), so this shows login history, not a live online status.</p>
        </div>

        <div className="mt-4">
          <h3 className="font-semibold text-slate-800 flex items-center gap-1.5 mb-2"><CalendarCheck className="h-4 w-4" /> Attendance (date range)</h3>
          <AttendanceRangeView studentId={s.id} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
const Field = ({ label, value }) => (<div className="flex gap-2 py-0.5"><span className="text-slate-400 w-28 shrink-0">{label}</span><span className="text-slate-800 font-medium break-words">{value || "—"}</span></div>);

export default function StudentsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [klass, setKlass] = useState("all");
  const [teacher, setTeacher] = useState("all");
  const [life, setLife] = useState("active");
  const [view, setView] = useState("grid");
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/students/directory"); setData(data); }
    catch { toast.error("Could not load students"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.students.filter((s) => s.status !== "merged" &&
      (life === "all" || (life === "left") === !!s.left) &&
      (klass === "all" || s.student_class === klass) &&
      (teacher === "all" || (s.teacher_ids || []).includes(teacher)) &&
      (!term || `${s.roll_number} ${s.name}`.toLowerCase().includes(term)));
  }, [data, q, klass, teacher, life]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="students-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><User className="h-5 w-5" /> Students</h2>
          <p className="text-sm text-slate-500">All student profiles with photos, details, status, stars, fees, attendance and login activity.</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          <button onClick={() => setView("grid")} className={`p-1.5 rounded ${view === "grid" ? "bg-white shadow" : ""}`} data-testid="view-grid"><LayoutGrid className="h-4 w-4" /></button>
          <button onClick={() => setView("table")} className={`p-1.5 rounded ${view === "table" ? "bg-white shadow" : ""}`} data-testid="view-table"><List className="h-4 w-4" /></button>
        </div>
      </div>
      <RecordHealth onFixed={load} />
      <IncompleteProfiles onOpen={(p) => { const s = data.students.find((x) => x.id === p.id); if (s) setSel(s); }} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or roll…" className="pl-9" data-testid="students-search" /></div>
        <Select value={klass} onValueChange={setKlass}><SelectTrigger className="w-[150px]" data-testid="filter-class"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All classes</SelectItem>{data.classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        <Select value={teacher} onValueChange={setTeacher}><SelectTrigger className="w-[160px]" data-testid="filter-teacher"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All teachers</SelectItem>{data.teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
        <Select value={life} onValueChange={setLife}><SelectTrigger className="w-[130px]" data-testid="filter-left"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="left">Left</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select>
      </div>
      <div className="text-sm text-slate-500" data-testid="students-count">{rows.length} student{rows.length === 1 ? "" : "s"}</div>

      {view === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {rows.map((s) => {
            const acct = ACCT[s.account_status] || ACCT.needs_password;
            return (
              <button key={s.id} onClick={() => setSel(s)} className="text-left rounded-2xl border border-slate-200 bg-white p-3 hover:border-blue-400 hover:shadow transition-all" data-testid={`student-card-${s.id}`}>
                <div className="flex items-center gap-3">
                  <AuthImg sid={s.id} hasPhoto={s.has_photo} name={s.name} className="h-12 w-12 rounded-full shrink-0" />
                  <div className="min-w-0"><div className="font-semibold text-slate-800 truncate">{s.name || "—"}</div><div className="text-xs text-slate-500">{s.roll_number} · {s.student_class}</div></div>
                </div>
                <div className="mt-2"><CompletionBar pct={s.completion} /></div>
                <div className="mt-2 flex gap-1"><Badge variant="outline" className={`text-[10px] ${acct.cls}`}>{acct.label}</Badge><LeftBadge s={s} /></div>
              </button>
            );
          })}
          {rows.length === 0 && <p className="col-span-full text-center text-slate-400 py-10">No students match your filters.</p>}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
              <th className="text-left px-3 py-2">Student</th><th className="text-left px-3 py-2">Roll</th><th className="text-left px-3 py-2">Class</th>
              <th className="text-left px-3 py-2">Teacher</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Profile</th><th className="text-left px-3 py-2">Last login</th>
            </tr></thead>
            <tbody>
              {rows.map((s) => {
                const acct = ACCT[s.account_status] || ACCT.needs_password;
                return (
                  <tr key={s.id} onClick={() => setSel(s)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`student-row-${s.id}`}>
                    <td className="px-3 py-2"><div className="flex items-center gap-2"><AuthImg sid={s.id} hasPhoto={s.has_photo} name={s.name} className="h-8 w-8 rounded-full shrink-0" /><span className="font-medium text-slate-800">{s.name || "—"}</span></div></td>
                    <td className="px-3 py-2">{s.roll_number}</td><td className="px-3 py-2">{s.student_class}</td>
                    <td className="px-3 py-2 text-slate-600">{s.teachers?.join(", ") || "—"}</td>
                    <td className="px-3 py-2"><span className="flex gap-1"><Badge variant="outline" className={`text-[10px] ${acct.cls}`}>{acct.label}</Badge><LeftBadge s={s} /></span></td>
                    <td className="px-3 py-2"><span className="font-semibold">{s.completion}%</span></td>
                    <td className="px-3 py-2 text-xs text-slate-500">{fmtIST(s.last_login)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && <p className="text-center text-slate-400 py-10">No students match your filters.</p>}
        </div>
      )}

      {sel && <StudentDetail s={sel} onClose={() => setSel(null)} onSaved={load} />}
    </div>
  );
}
